import { ResponsiveFilters } from '@/components/responsive-filters';
import { useMemo } from 'react';
import { createDataTable } from '@/components/data-table';
import { Dropdown } from '@/components/dropdowns';
import { Icons } from '@/components/primitives';
import { useProviderDrift } from '@/api/config-hooks';
import { TZ_LABEL, ago, fmtClock, fmtDateShort } from '@/lib/datetime';
import {
  DRIFT_WINDOW_LABEL,
  DRIFT_WINDOWS,
  driftObjectLabel,
  driftSideLabel,
  driftTargetLabel,
  providerDriftQuery,
  summarizeCoverage,
  emptyDriftMessage,
  toDriftRows,
  DRIFT_FINDING_LABEL,
  driftCoverageLabel,
} from '@/lib/provider-drift';
import { DRIFT_SCREEN_DEFAULT_WINDOW, DRIFT_STATE_LABEL } from '@/lib/provider-drift';
import type { DriftRow, DriftSide, DriftState, DriftWindow } from '@/lib/provider-drift';

const ROW_H = 52;

const { Table } = createDataTable([
  { key: 'first', header: `First seen (${TZ_LABEL})`, width: 150, priority: 3,
    title: 'When the sweep first saw this field diverge' },
  { key: 'last', header: 'Last seen', width: 130, priority: 2,
    title: 'The most recent pass that still saw it diverge' },
  { key: 'campaign', header: 'Campaign', width: 200 },
  { key: 'target', header: 'Target', width: 84, align: 'center' },
  { key: 'object', header: 'Object', width: 190, priority: 3 },
  { key: 'field', header: 'Field', width: 190,
    title: 'The ladder’s own field name, not the provider’s' },
  { key: 'values', header: 'Expected → observed', width: 320,
    title: 'Top: what SAM has saved. Bottom: what the provider held.' },
  { key: 'state', header: 'State', width: 180 },
]);

// ── vocabulary chips ────────────────────────────────────────────────────────

// Coloured per provider, matching the channel palette the rest of the app uses
// for the same two words.
const TARGET_COLOR: Record<string, string> = {
  meta: 'var(--chart-1)',
  ttd: 'var(--chart-3)',
};

function TargetPill({ target }: { target: string }) {
  const color = TARGET_COLOR[target] ?? 'var(--ink-3)';
  return (
    <span
      className="mono"
      style={{
        fontSize: 9.5,
        letterSpacing: '.08em',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: 3,
        color,
        background: `color-mix(in oklch, ${color} 14%, transparent)`,
        border: `1px solid color-mix(in oklch, ${color} 35%, transparent)`,
      }}
    >
      {driftTargetLabel(target)}
    </span>
  );
}

/**
 * One side of the comparison.
 *
 * An absent value renders as a SENTENCE, italic and muted, never as a dash: the
 * two sides mean different things when they are missing and both are findings
 * in their own right. The visual weight is deliberately asymmetric — expected is
 * the reference, observed is what actually happened — so the eye lands on the
 * provider's value.
 */
function SideValue({ value, side }: { value: string | null; side: DriftSide }) {
  const absent = value == null;
  return (
    <span
      className="mono"
      style={{
        fontSize: 11,
        fontStyle: absent ? 'italic' : 'normal',
        color: absent
          ? 'var(--ink-3)'
          : side === 'expected'
            ? 'var(--ink-2)'
            : 'var(--ink-1)',
        fontWeight: side === 'observed' && !absent ? 600 : 400,
        // Both sides sit inside flex containers, and `td > *` only reaches the
        // cell's IMMEDIATE children — without this a long value refuses to
        // ellipsize and pushes the column instead.
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={value ?? undefined}
    >
      {driftSideLabel(value, side)}
    </span>
  );
}

const STATE_COLOR: Record<DriftState, string> = {
  open: 'var(--warn)',
  repaired: 'var(--good)',
  resolved: 'var(--good)',
  superseded: 'var(--ink-3)',
};

const STATE_MARK: Record<DriftState, string> = {
  open: '■',
  repaired: '✓',
  resolved: '○',
  superseded: '—',
};

/**
 * The second line says HOW the row closed, because the four states answer
 * different questions: REPAIRED means SAM wrote the provider and read it back,
 * RESOLVED means SAM wrote nothing and the provider came back in line anyway
 * (a human, or the provider itself), SUPERSEDED closes a former expectation. Retired objects continue to be checked
 * under their PAUSED expectation; unresolved findings may be detect-only.
 */
function stateDetail(row: DriftRow): string {
  const e = row.evidence;
  if (e) return [e.lifecycle, e.reason, e.replacement_id ? `Replacement ${e.replacement_id}` : '',
    e.last_verified_at ? `Verified ${ago(Number(e.last_verified_at))}` : 'Verification pending',
    e.freshness === 'overdue' ? 'Verification overdue' : '',
    e.repair ? `${e.repair.attempts}/10 failed attempts${e.repair.next_retry_at ? ` · retry ${fmtClock(Number(e.repair.next_retry_at))}` : ''}` : '',
  ].filter(Boolean).join(' · ');

  switch (row.state) {
    case 'open':
      return `open since ${fmtClock(row.detectedAtMs)} · nothing claimed it`;
    case 'repaired':
      return `by SAM · ${fmtClock(row.repairedAtMs ?? 0)} · ${ago(row.repairedAtMs ?? 0)}`;
    case 'resolved':
      return `seen back in line ${fmtClock(row.resolvedAtMs ?? 0)} · nothing in SAM wrote it`;
    case 'superseded':
      return `plan revision ${row.campaignRevision} replaced · ${ago(row.resolvedAtMs ?? 0)}`;
  }
}

function StateCell({ row }: { row: DriftRow }) {
  const color = STATE_COLOR[row.state];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            letterSpacing: '.08em',
            fontWeight: 700,
            color,
          }}
        >
          {STATE_MARK[row.state]} {row.evidence ? DRIFT_FINDING_LABEL[row.evidence.status] : DRIFT_STATE_LABEL[row.state]}
        </span>
        {row.hasPreImage && (
          // Only ever true for Meta, whose targeting write replaces the whole
          // object.
          //
          // This said "captured and is recoverable". It is captured; it is not
          // recoverable by anything in this product. No route returns the body,
          // and — the part that makes it more than a missing endpoint — there is
          // no verb anywhere that could apply an arbitrary targeting object
          // back: every Meta targeting write goes through
          // `build_ad_set_targeting`. Restoring one means a human reading the
          // row out of D1 and doing it in Ads Manager. A badge that implies a
          // one-click undo is worse than no badge, so the tooltip says exactly
          // what exists and where it lives.
          <span
            className="mono"
            title="A full copy of the provider object was captured in provider_drift_preimages before the repair overwrote it. There is no restore action — recovering it means reading the row and applying it at the provider by hand."
            style={{
              fontSize: 9,
              letterSpacing: '.06em',
              fontWeight: 700,
              padding: '1px 4px',
              borderRadius: 3,
              color: 'var(--ink-3)',
              border: '1px solid var(--line)',
            }}
          >
            PRE-IMAGE
          </span>
        )}
      </div>
      <span className="muted mono" style={{ fontSize: 10.5 }}>
        {stateDetail(row)}
      </span>
    </div>
  );
}

// ── the screen ──────────────────────────────────────────────────────────────

export interface ProviderDriftViewProps {
  /** The fetch window. A PROP, not state — see the note in the route. */
  filters?: { view: 'unresolved' | 'history'; target?: string; lifecycle?: string; status?: string; cursor?: string };
  onFiltersChange?: (next: Partial<NonNullable<ProviderDriftViewProps['filters']>>) => void;
  driftWindow: DriftWindow;
  /** Narrows the read to one campaign. Also a prop, for the same reason. */
  campaignId: string | undefined;
  onWindowChange: (next: DriftWindow) => void;
  onCampaignChange: (next: string | undefined) => void;
}

export function ProviderDriftView({
  driftWindow,
  filters = { view: 'unresolved' },
  onFiltersChange = () => {},
  campaignId,
  onWindowChange,
  onCampaignChange,
}: ProviderDriftViewProps) {
  const query = useMemo(
    () => providerDriftQuery(driftWindow, campaignId, filters),
    [driftWindow, campaignId, filters],
  );
  const driftResult = useProviderDrift(query);

  const rows = useMemo(
    () => toDriftRows(driftResult.data?.events ?? []),
    [driftResult.data],
  );
  const visible = rows;
  const summary = driftResult.data?.summary;
  const coverage = useMemo(
    () => summarizeCoverage(driftResult.data?.scans ?? []),
    [driftResult.data],
  );
  const truncated = driftResult.data?.truncated === true;

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Provider drift</h1>
        </div>
      </div>

      {/* WINDOW sits beside the client-side filters the way Audit's WHEN range
          does, even though it alone re-reads the server — an operator reaches
          for "show me more" in one place, and the summary strip below says how
          much came back.

          No `width` on either: the menu falls back to
          max(trigger, DROPDOWN_MIN_WIDTH_PX), which fits these labels. */}
      <ResponsiveFilters className="filter-bar">
        <Dropdown
          label={filters.view === 'history' ? "HISTORY WINDOW" : "HISTORY WINDOW (when viewing history)"}
          value={driftWindow}
          data-filter-default={DRIFT_SCREEN_DEFAULT_WINDOW}
          options={DRIFT_WINDOWS.map((w) => ({ id: w, label: DRIFT_WINDOW_LABEL[w] }))}
          onChange={(next: string) => onWindowChange(next as DriftWindow)}
        />
        <Dropdown
          label="TARGET"
          value={filters.target ?? 'all'}
          options={[
            { id: 'all', label: 'Both providers' },
            { id: 'meta', label: 'Meta' },
            { id: 'ttd', label: 'The Trade Desk' },
          ]}
          onChange={(next: string) => onFiltersChange({ target: next === 'all' ? undefined : next })}
        />
        <Dropdown label="VIEW" data-filter-default="unresolved" value={filters.view} options={[{ id: 'unresolved', label: 'Unresolved — all ages' }, { id: 'history', label: 'History' }]} onChange={(view: string) => onFiltersChange({ view: view as 'unresolved' | 'history' })} />
        <Dropdown label="LIFECYCLE" value={filters.lifecycle ?? 'all'} options={['all', 'current', 'retired', 'safety'].map(id => ({ id, label: id === 'all' ? 'All objects' : id }))} onChange={(next: string) => onFiltersChange({ lifecycle: next === 'all' ? undefined : next })} />
        <Dropdown label="STATE" value={filters.status ?? 'all'} options={[{ id: 'all', label: 'All states' }, ...Object.entries(DRIFT_FINDING_LABEL).map(([id, label]) => ({ id, label }))]} onChange={(next: string) => onFiltersChange({ status: next === 'all' ? undefined : next })} />
        {campaignId && (
          // The campaign filter is server-side, so clearing it is a refetch and
          // not a client-side widening — worth saying, because it is the escape
          // hatch from a truncated window.
          <span
            className="mono"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              padding: '4px 8px',
              borderRadius: 3,
              background: 'var(--bg-sunk)',
              border: '1px solid var(--line)',
            }}
          >
            {/* The filter bar does not wrap, so a long id has to ellipsize
                here rather than push the dismiss button off the row. */}
            <span
              title={campaignId}
              style={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              campaign {campaignId}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ padding: '0 4px' }}
              title="Show every campaign in this window"
              onClick={() => onCampaignChange(undefined)}
            >
              {Icons.x}
            </button>
          </span>
        )}
      </ResponsiveFilters>

      <div
        className="mono"
        style={{
          padding: '10px 22px',
          background: 'var(--bg-elev)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 10.5,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        <span>
          {summary ? `${Number(summary.total).toLocaleString()} findings · ${Number(summary.unresolved).toLocaleString()} unresolved · ${Number(summary.known_limitations).toLocaleString()} known limitations · ${Number(summary.campaigns).toLocaleString()} campaigns` : 'Counts unavailable'}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ textTransform: 'none', letterSpacing: 0 }}>
          Findings preserve the evidence, lifecycle, and outcome of each check.
        </span>
      </div>

      <div className="filter-bar" aria-label="Drift coverage" style={{ display: 'grid', gap: 8 }}>
        {(driftResult.data?.scans ?? []).map(scan => <span key={`${scan.campaign_id}:${scan.target}`} className="mono" title={scan.last_error ?? undefined}>
          {scan.campaign_id} · {driftTargetLabel(scan.target)}: {driftCoverageLabel(scan)}
          {scan.coverage ? ` · current ${scan.coverage.current_verified}/${scan.coverage.current_expected} · retired ${scan.coverage.retired_verified}/${scan.coverage.retired_expected}` : ''}
        </span>)}
      </div>
      {(truncated || filters.cursor) && <div className="filter-bar">
        {filters.cursor && <button className="btn btn-sm" onClick={() => onFiltersChange({ cursor: undefined })}>First page</button>}
        {driftResult.data?.next_cursor && <button className="btn btn-sm" onClick={() => onFiltersChange({ cursor: driftResult.data?.next_cursor ?? undefined })}>Next page</button>}
        <span>Showing {rows.length} of {Number(summary?.total ?? rows.length)} findings</span>
      </div>}

      <Table
        // A comparison matrix: expected against observed, side by side. It stays a
        // grid on phones and scrolls sideways rather than becoming cards.
        keepTableOnMobile
        label="Provider drift findings"
        rows={visible}
        rowH={ROW_H}
        unfilteredTotal={rows.length}
        deps={[{ what: 'provider drift', query: driftResult }]}
        empty={emptyDriftMessage(coverage, Boolean(campaignId))}
        noMatch="No findings match these filters."
        // Tinting the whole row, not just its state cell: open drift is the
        // only actionable state on this screen, and it has to be findable
        // without reading the last column of every line.
        //
        // Mixed against `--panel` rather than `transparent` because the row's
        // own background IS `--panel` (styles.css:1874) — mixing to transparent
        // would let the tbody show through and wash the tint out. The inset
        // shadow is a left bar that costs no layout, since a `border-left` on a
        // grid row would shift every cell in it.
        rowStyle={(row) =>
          row.open
            ? {
                background: 'color-mix(in oklch, var(--warn) 12%, var(--panel))',
                boxShadow: 'inset 3px 0 0 var(--warn)',
              }
            : undefined
        }
        renderRow={(row) => (
          <>
            <td className="mono" style={{ fontSize: 11, lineHeight: 1.25 }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>
                  {fmtDateShort(row.detectedAtMs)} · {fmtClock(row.detectedAtMs)}
                </span>
                <span className="muted mono" style={{ fontSize: 10.5, marginTop: 1 }}>
                  {ago(row.detectedAtMs)}
                </span>
              </div>
            </td>
            <td className="mono" style={{ fontSize: 11, lineHeight: 1.25 }}>
              {/* Same instant as the first sighting on a one-pass row; a moving
                  last sighting is what "still true" looks like. */}
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontWeight: row.lastDetectedAtMs > row.detectedAtMs ? 600 : 400 }}>
                  {fmtDateShort(row.lastDetectedAtMs)} · {fmtClock(row.lastDetectedAtMs)}
                </span>
                <span className="muted mono" style={{ fontSize: 10.5, marginTop: 1 }}>
                  {row.lastDetectedAtMs > row.detectedAtMs
                    ? `still seen ${ago(row.lastDetectedAtMs)}`
                    : 'seen once'}
                </span>
              </div>
            </td>
            <td style={{ lineHeight: 1.25 }}>
              <button
                type="button"
                className="mono"
                title="Show only this campaign"
                onClick={() => onCampaignChange(row.campaignId)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  background: 'none',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'inherit',
                  maxWidth: '100%',
                }}
              >
                <span
                  style={{
                    fontSize: 11.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {row.campaignId}
                </span>
                <span className="muted mono" style={{ fontSize: 10.5, marginTop: 1 }}>
                  rev {row.campaignRevision}
                </span>
              </button>
            </td>
            <td className="center">
              <TargetPill target={row.target} />
            </td>
            <td className="mono" style={{ fontSize: 11, lineHeight: 1.25 }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span>{driftObjectLabel(row.objectKind)}</span>
                <span
                  className="muted mono"
                  style={{
                    fontSize: 10.5,
                    marginTop: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={row.providerObjectId}
                >
                  {row.providerObjectId}
                </span>
              </div>
            </td>
            <td
              className="mono"
              style={{
                fontSize: 11.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={row.field}
            >
              {row.field}
            </td>
            <td style={{ lineHeight: 1.25 }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>
                <SideValue value={row.expected} side="expected" />
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span className="muted mono" style={{ fontSize: 10 }}>→</span>
                  <SideValue value={row.observed} side="observed" />
                </span>
              </div>
            </td>
            <td>
              <StateCell row={row} />
            </td>
          </>
        )}
      />
    </div>
  );
}
